import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophone, faXmark } from '@fortawesome/free-solid-svg-icons';
import { API_BASE, getAuthHeaders } from '../services/api';

const ACCENT_COLOR = '#fec35c';
const DARK_COLOR = '#444444';

type VoiceState = 'idle' | 'listening' | 'transcribing' | 'thinking' | 'generating' | 'speaking';

// Fun processing messages for each phase
const TRANSCRIBING_MESSAGES = [
  'Listening to your words...',
  'Converting speech to text...',
  'Decoding your message...',
];

const THINKING_MESSAGES = [
  'Lupa is thinking...',
  'Diving into the knowledge base...',
  'Consulting the documentation...',
  'Searching for the best answer...',
  'Connecting the dots...',
  'Pondering your question...',
];

const GENERATING_MESSAGES = [
  'Crafting a response...',
  'Preparing to speak...',
  'Finding the right words...',
  'Getting ready to answer...',
];

interface VoiceInterfaceProps {
  isOpen: boolean;
  onClose: () => void;
  agentId: string;
  /**
   * Callback to get the current session ID at call time.
   * This is called when the voice request is initiated (not when the component mounts)
   * to ensure we always use the current session, not a stale closure value.
   */
  getSessionId: () => string;
  onTranscript?: (text: string) => void;
  /**
   * Called when voice processing completes.
   * @param transcript - The user's transcribed speech
   * @param response - The assistant's response
   * @param sessionId - The session ID that was used for this request (for validation)
   */
  onVoiceComplete?: (transcript: string, response: string, sessionId: string) => void;
  clientId?: string;
}

export const VoiceInterface: React.FC<VoiceInterfaceProps> = ({
  isOpen,
  onClose,
  agentId,
  getSessionId,
  onTranscript,
  onVoiceComplete,
  clientId,
}) => {
  // Store the session ID that was active when recording started
  // This ensures the response goes to the correct conversation even if user switches
  const sessionIdAtRecordingStart = useRef<string>('');
  const [voiceState, setVoiceState] = useState<VoiceState>('idle');
  const [amplitude, setAmplitude] = useState(0);
  const [transcript, setTranscript] = useState('');
  const [response, setResponse] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [processingMessage, setProcessingMessage] = useState('');
  const processingMessageIndexRef = useRef(0);
  const processingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Audio recording refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Audio playback refs - separate context to avoid conflicts
  const playbackContextRef = useRef<AudioContext | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Helper to start rotating messages for a phase
  const startProcessingMessages = useCallback((messages: string[]) => {
    processingMessageIndexRef.current = 0;
    setProcessingMessage(messages[0]);

    if (processingIntervalRef.current) {
      clearInterval(processingIntervalRef.current);
    }

    processingIntervalRef.current = setInterval(() => {
      processingMessageIndexRef.current = (processingMessageIndexRef.current + 1) % messages.length;
      setProcessingMessage(messages[processingMessageIndexRef.current]);
    }, 2000); // Rotate every 2 seconds
  }, []);

  // Helper to stop rotating messages
  const stopProcessingMessages = useCallback(() => {
    if (processingIntervalRef.current) {
      clearInterval(processingIntervalRef.current);
      processingIntervalRef.current = null;
    }
    setProcessingMessage('');
  }, []);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (recordingContextRef.current && recordingContextRef.current.state !== 'closed') {
      recordingContextRef.current.close();
      recordingContextRef.current = null;
    }
    if (playbackContextRef.current && playbackContextRef.current.state !== 'closed') {
      playbackContextRef.current.close();
      playbackContextRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    stopProcessingMessages();
  }, [stopProcessingMessages]);

  // Reset state when opening/closing
  useEffect(() => {
    if (isOpen) {
      // Reset to clean state when opening
      setVoiceState('idle');
      setAmplitude(0);
      setTranscript('');
      setResponse('');
      setError(null);
    } else {
      // Cleanup when closing
      cleanup();
      setVoiceState('idle');
      setAmplitude(0);
      setTranscript('');
      setResponse('');
      setError(null);
    }
    return cleanup;
  }, [isOpen, cleanup]);

  // Amplitude analyzer
  const analyzeAmplitude = useCallback(() => {
    if (!analyserRef.current) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);

    // Calculate average amplitude
    const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
    const normalizedAmplitude = average / 255; // Normalize to 0-1

    setAmplitude(normalizedAmplitude);

    if (voiceState === 'listening' || voiceState === 'speaking') {
      animationFrameRef.current = requestAnimationFrame(analyzeAmplitude);
    }
  }, [voiceState]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    setVoiceState('transcribing');
    startProcessingMessages(TRANSCRIBING_MESSAGES);
  }, [startProcessingMessages]);

  // Play TTS audio
  const playAudio = useCallback(async (base64Audio: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      try {
        // Create a fresh Audio element each time to avoid MediaElementSourceNode issues
        // (a MediaElementSourceNode can only be created once per HTMLMediaElement)
        const audio = new Audio();
        audio.src = `data:audio/mp3;base64,${base64Audio}`;

        // Set up a dedicated playback context (create once and reuse)
        if (!playbackContextRef.current || playbackContextRef.current.state === 'closed') {
          playbackContextRef.current = new AudioContext();
        }

        // Create source node for this new audio element
        const source = playbackContextRef.current.createMediaElementSource(audio);
        analyserRef.current = playbackContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 256;

        source.connect(analyserRef.current);
        analyserRef.current.connect(playbackContextRef.current.destination);

        // Store reference for cleanup
        audioRef.current = audio;

        audio.onended = () => {
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
          }
          setAmplitude(0);
          resolve();
        };

        audio.onerror = () => {
          reject(new Error('Audio playback failed'));
        };

        audio.play();
        animationFrameRef.current = requestAnimationFrame(analyzeAmplitude);

      } catch (err) {
        reject(err);
      }
    });
  }, [analyzeAmplitude]);

  // Process audio: STT -> Agent -> TTS
  const processAudio = useCallback(async (audioBlob: Blob) => {
    try {
      // Already in 'transcribing' state from stopRecording

      // Use the session ID that was captured when recording started
      // NOT the current session (which may have changed if user switched conversations)
      const sessionId = sessionIdAtRecordingStart.current;
      console.log(`[VoiceInterface] Processing audio for session: ${sessionId}`);

      // Create FormData for the voice request
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('agent', agentId);
      formData.append('sessionId', sessionId);
      if (clientId) formData.append('clientId', clientId);

      // Transition to thinking phase after a brief delay to show transcribing
      let thinkingTimeout: ReturnType<typeof setTimeout> | null = setTimeout(() => {
        setVoiceState('thinking');
        startProcessingMessages(THINKING_MESSAGES);
      }, 1500);

      try {
        // Send to voice endpoint
        const response = await fetch(`${API_BASE}/voice`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: formData,
        });

        // Clear the timeout now that we have a response
        if (thinkingTimeout) {
          clearTimeout(thinkingTimeout);
          thinkingTimeout = null;
        }

        if (!response.ok) {
          throw new Error(`Voice request failed: ${response.status}`);
        }

        const data = await response.json();

        // Update transcript
        setTranscript(data.transcript || '');
        if (onTranscript && data.transcript) {
          onTranscript(data.transcript);
        }

        // Update response text
        setResponse(data.response || '');

        // Notify parent that voice exchange is complete (for adding to chat history)
        // Pass the session ID so parent can validate and route to correct conversation
        if (onVoiceComplete && data.transcript && data.response) {
          onVoiceComplete(data.transcript, data.response, sessionId);
        }

        // Play TTS audio if available
        if (data.audio_base64) {
          setVoiceState('generating');
          startProcessingMessages(GENERATING_MESSAGES);

          // Brief pause to show generating message
          await new Promise(resolve => setTimeout(resolve, 800));

          stopProcessingMessages();
          setVoiceState('speaking');
          await playAudio(data.audio_base64);
        }

        stopProcessingMessages();
        setVoiceState('idle');

      } catch (err) {
        // Always clear the timeout on error to prevent state corruption
        if (thinkingTimeout) {
          clearTimeout(thinkingTimeout);
          thinkingTimeout = null;
        }
        throw err; // Re-throw to be caught by outer catch
      }

    } catch (err) {
      console.error('Error processing audio:', err);
      setError('Failed to process voice. Please try again.');
      stopProcessingMessages();
      setVoiceState('idle');
    }
  }, [agentId, clientId, onTranscript, onVoiceComplete, startProcessingMessages, stopProcessingMessages, playAudio]);

  // Start recording
  const startRecording = useCallback(async () => {
    try {
      setError(null);
      setTranscript('');
      setResponse('');

      // Capture the session ID at the moment recording starts
      // This is the session that will receive the voice messages
      sessionIdAtRecordingStart.current = getSessionId();
      console.log(`[VoiceInterface] Recording started for session: ${sessionIdAtRecordingStart.current}`);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        }
      });
      streamRef.current = stream;

      // Set up audio context for amplitude analysis (recording)
      recordingContextRef.current = new AudioContext();
      analyserRef.current = recordingContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;

      const source = recordingContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);

      // Set up MediaRecorder
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        await processAudio(audioBlob);
      };

      mediaRecorderRef.current.start(100); // Collect data every 100ms
      setVoiceState('listening');

      // Start amplitude analysis
      animationFrameRef.current = requestAnimationFrame(analyzeAmplitude);

    } catch (err) {
      console.error('Error starting recording:', err);
      setError('Could not access microphone. Please check permissions.');
      setVoiceState('idle');
    }
  }, [analyzeAmplitude, getSessionId, processAudio]);

  // Handle click on the main circle
  const handleCircleClick = () => {
    if (voiceState === 'idle') {
      startRecording();
    } else if (voiceState === 'listening') {
      stopRecording();
    }
  };

  // Check if currently in any processing state
  const isProcessing = voiceState === 'transcribing' || voiceState === 'thinking' || voiceState === 'generating';

  // Calculate pulsating circle size based on amplitude and state
  const getCircleScale = () => {
    const baseScale = 1;
    const amplitudeScale = amplitude * 0.5; // Max 50% increase from amplitude

    if (voiceState === 'listening') {
      return baseScale + amplitudeScale + 0.1; // Slightly larger when listening
    } else if (voiceState === 'speaking') {
      return baseScale + amplitudeScale;
    } else if (isProcessing) {
      return baseScale + 0.05; // Subtle pulse during processing
    }
    return baseScale;
  };

  // Get circle color based on state
  const getCircleColor = () => {
    switch (voiceState) {
      case 'listening':
        return ACCENT_COLOR;
      case 'transcribing':
        return '#F59E0B'; // Amber for transcribing
      case 'thinking':
        return '#8B5CF6'; // Purple for thinking
      case 'generating':
        return '#06B6D4'; // Cyan for generating
      case 'speaking':
        return '#10B981'; // Green for speaking
      default:
        return DARK_COLOR;
    }
  };

  // Get status text
  const getStatusText = () => {
    switch (voiceState) {
      case 'listening':
        return 'Listening... Click to stop';
      case 'transcribing':
      case 'thinking':
      case 'generating':
        return processingMessage || 'Processing...';
      case 'speaking':
        return 'Lupa is speaking...';
      default:
        return 'Click to start speaking';
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        backdropFilter: 'blur(8px)',
      }}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          position: 'absolute',
          top: '2rem',
          right: '2rem',
          background: 'rgba(255, 255, 255, 0.1)',
          border: 'none',
          borderRadius: '50%',
          width: '48px',
          height: '48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'background-color 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
        }}
      >
        <FontAwesomeIcon icon={faXmark} style={{ color: 'white', fontSize: '1.5rem' }} />
      </button>

      {/* Main pulsating circle */}
      <div
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Outer glow rings */}
        {(voiceState === 'listening' || voiceState === 'speaking') && (
          <>
            <div
              style={{
                position: 'absolute',
                width: '280px',
                height: '280px',
                borderRadius: '50%',
                backgroundColor: getCircleColor(),
                opacity: 0.1 + amplitude * 0.15,
                transform: `scale(${1 + amplitude * 0.3})`,
                transition: 'transform 0.05s ease-out, opacity 0.05s ease-out',
              }}
            />
            <div
              style={{
                position: 'absolute',
                width: '240px',
                height: '240px',
                borderRadius: '50%',
                backgroundColor: getCircleColor(),
                opacity: 0.15 + amplitude * 0.2,
                transform: `scale(${1 + amplitude * 0.2})`,
                transition: 'transform 0.05s ease-out, opacity 0.05s ease-out',
              }}
            />
          </>
        )}

        {/* Main interactive circle */}
        <button
          onClick={handleCircleClick}
          disabled={isProcessing || voiceState === 'speaking'}
          style={{
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            backgroundColor: getCircleColor(),
            border: 'none',
            cursor: isProcessing || voiceState === 'speaking' ? 'default' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: `scale(${getCircleScale()})`,
            transition: 'transform 0.1s ease-out, background-color 0.3s ease',
            boxShadow: `0 0 60px ${getCircleColor()}50`,
          }}
        >
          <FontAwesomeIcon
            icon={faMicrophone}
            style={{
              color: 'white',
              fontSize: '4rem',
              opacity: isProcessing ? 0.5 : 1,
            }}
          />
        </button>

        {/* Processing spinner overlay */}
        {isProcessing && (
          <div
            style={{
              position: 'absolute',
              width: '220px',
              height: '220px',
              borderRadius: '50%',
              border: '4px solid transparent',
              borderTopColor: 'white',
              animation: 'spin 1s linear infinite',
            }}
          />
        )}
      </div>

      {/* Status text */}
      <p
        style={{
          color: 'white',
          fontSize: '1.25rem',
          marginTop: '2rem',
          fontFamily: 'Lato, system-ui, sans-serif',
          opacity: 0.9,
        }}
      >
        {getStatusText()}
      </p>

      {/* Transcript display */}
      {transcript && (
        <div
          style={{
            marginTop: '1.5rem',
            padding: '1rem 2rem',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '4px',
            maxWidth: '600px',
            textAlign: 'center',
          }}
        >
          <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.875rem', margin: '0 0 0.5rem 0' }}>
            You said:
          </p>
          <p style={{ color: 'white', fontSize: '1rem', margin: 0, fontStyle: 'italic' }}>
            "{transcript}"
          </p>
        </div>
      )}

      {/* Response display */}
      {response && (
        <div
          style={{
            marginTop: '1rem',
            padding: '1rem 2rem',
            backgroundColor: 'rgba(254, 195, 92, 0.2)',
            borderRadius: '4px',
            maxWidth: '600px',
            textAlign: 'center',
          }}
        >
          <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.875rem', margin: '0 0 0.5rem 0' }}>
            Lupa:
          </p>
          <p style={{ color: 'white', fontSize: '1rem', margin: 0 }}>
            {response}
          </p>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div
          style={{
            marginTop: '1.5rem',
            padding: '1rem 2rem',
            backgroundColor: 'rgba(239, 68, 68, 0.2)',
            borderRadius: '4px',
            maxWidth: '600px',
            textAlign: 'center',
          }}
        >
          <p style={{ color: '#FCA5A5', fontSize: '1rem', margin: 0 }}>
            {error}
          </p>
        </div>
      )}

      {/* CSS animation for spinner */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default VoiceInterface;
